import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Heading, Text } from '@indeed/ifl-components';
import { buildIrisSimulatorCommand, type IrisSimulatorPlatform } from './irisSimulatorCommand';

export interface IrisSimulatorCommandsProps {
    locale: string;
    proctorGroups: string;
    accountEmail: string;
}

const COPY_FEEDBACK_MS = 2000;

const CLIPBOARD_API_TIMEOUT_MS = 1000;

const copyTextToClipboard = async (text: string): Promise<boolean> => {
    try {
        await Promise.race([
            navigator.clipboard.writeText(text),
            new Promise<never>((_, reject) => {
                window.setTimeout(
                    () => reject(new Error('clipboard timeout')),
                    CLIPBOARD_API_TIMEOUT_MS,
                );
            }),
        ]);
        return true;
    } catch {
        // Clipboard API missing, blocked, or hung waiting for permission.
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    try {
        return document.execCommand('copy');
    } catch {
        return false;
    } finally {
        document.body.removeChild(textarea);
    }
};

interface CopyableIrisCommandProps {
    platform: IrisSimulatorPlatform;
    label: string;
    command: string;
}

const CopyableIrisCommand: React.FC<CopyableIrisCommandProps> = ({ platform, label, command }) => {
    const [copied, setCopied] = useState(false);
    const [copyFailed, setCopyFailed] = useState(false);
    const [copying, setCopying] = useState(false);
    const commandRef = useRef(command);
    commandRef.current = command;
    const copyButtonLabel = `Copy ${label} command`;

    useEffect(() => {
        setCopied(false);
        setCopyFailed(false);
        setCopying(false);
    }, [command]);

    useEffect(() => {
        if (!copied && !copyFailed) {
            return undefined;
        }
        const timeoutId = window.setTimeout(() => {
            setCopied(false);
            setCopyFailed(false);
        }, COPY_FEEDBACK_MS);
        return () => window.clearTimeout(timeoutId);
    }, [copied, copyFailed]);

    const handleCopy = (): void => {
        const commandToCopy = command;
        setCopying(true);
        setCopied(false);
        setCopyFailed(false);
        void copyTextToClipboard(commandToCopy).then((ok) => {
            if (commandRef.current !== commandToCopy) {
                return;
            }
            setCopying(false);
            setCopied(ok);
            setCopyFailed(!ok);
        });
    };

    const buttonLabel = copying ? 'Copying…' : copied ? 'Copied' : 'Copy';
    const ariaLabel = copied
        ? `${label} command copied`
        : copying
          ? `Copying ${label} command`
          : copyButtonLabel;

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    marginBlockEnd: 2,
                }}
            >
                <Heading level={3} sx={{ margin: 0 }}>
                    {label}
                </Heading>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {copied && (
                        <Text role="status" variant="secondary" sx={{ fontSize: 'sm' }}>
                            Copied to clipboard
                        </Text>
                    )}
                    {copyFailed && (
                        <Text role="status" variant="secondary" sx={{ fontSize: 'sm' }}>
                            Copy failed
                        </Text>
                    )}
                    <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        data-testid={`iris-${platform}-copy`}
                        aria-label={ariaLabel}
                        disabled={copying}
                        onClick={handleCopy}
                    >
                        {buttonLabel}
                    </Button>
                </Box>
            </Box>
            <Box
                as="pre"
                data-testid={`iris-${platform}-command`}
                aria-label={`${label} Iris command`}
                sx={{
                    margin: 0,
                    padding: 4,
                    backgroundColor: 'background.weaker',
                    border: '1px solid',
                    borderColor: 'border.weak',
                    borderRadius: 'md',
                    fontFamily: 'mono',
                    fontSize: 'sm',
                    lineHeight: 1.8,
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'break-word',
                    userSelect: 'all',
                }}
            >
                {command}
            </Box>
        </Box>
    );
};

/** Copyable iOS and Android Iris CLI commands for the current RBS test config. */
export const IrisSimulatorCommands: React.FC<IrisSimulatorCommandsProps> = ({
    locale,
    proctorGroups,
    accountEmail,
}) => {
    const hasAccount = accountEmail.trim().length > 0;
    const iosCommand = buildIrisSimulatorCommand({
        platform: 'ios',
        locale,
        proctorGroups,
        accountEmail,
    });
    const androidCommand = buildIrisSimulatorCommand({
        platform: 'android',
        locale,
        proctorGroups,
        accountEmail,
    });

    return (
        <Box data-testid="iris-simulator-commands">
            <Heading level={2} sx={{ marginBlockEnd: 2 }}>
                Iris simulator
            </Heading>
            <Text variant="secondary" sx={{ marginBlockEnd: 4, display: 'block', fontSize: 'sm' }}>
                Paste a command into a terminal to launch JSMA with this page&apos;s locale, Proctor
                buckets, and test account. See the{' '}
                <a
                    href="https://code.corp.indeed.com/mobile-app-platform/iris#iris-cli"
                    target="_blank"
                    rel="noreferrer"
                >
                    Iris CLI
                </a>
                .
            </Text>
            {!hasAccount && (
                <Text
                    variant="secondary"
                    sx={{ marginBlockEnd: 4, display: 'block', fontSize: 'sm' }}
                >
                    Sign in to include <code>--account</code> and <code>--dump-network</code>.
                </Text>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <CopyableIrisCommand platform="ios" label="iOS" command={iosCommand} />
                <CopyableIrisCommand platform="android" label="Android" command={androidCommand} />
            </Box>
        </Box>
    );
};

export default IrisSimulatorCommands;
